/**
 * HelpPanel: the window a module's visual guide opens in. HelpButton (and HelpTitle) render it for you; use it
 * directly only for another trigger than the "?" or the "How it works" link:
 *
 *   import HelpPanel from "../../components/Help/HelpPanel";
 *   <HelpPanel open={open} onClose={() => setOpen(false)} id="matrix" title={t("help.matrix.title")}>
 *     <MatrixGuide />
 *   </HelpPanel>
 *
 * Props: open, onClose, title (the panel's heading), id (test ids: help-panel-<id>, help-body-<id>), children (the
 * guide, mounted only while the panel is open).
 * From md up a slide-over from the right (36rem, never wider than the screen), on phones the full screen; a header
 * ("Guide" above the title, and a close button), the guide in its own scroll box and one "Got it" button that
 * closes. Esc, a click outside and the close button close it too; the focus goes back to whatever opened it
 * (Headless UI Dialog). The slide respects prefers-reduced-motion.
 * Keyboard: the guide's scroll box is a focusable region (named by the title) and takes the focus first, so arrows
 * and Page Down scroll the guide at once; Tab goes box → "Got it" → close → box, so the box can always be reached
 * again.
 */
import React, { Fragment, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

export default function HelpPanel({ open, onClose, title, id, children }) {
  const [t] = useTranslation();
  const titleId = `help-title-${useId()}`;
  // The guide's scroll box takes the focus first: arrows and Page Down scroll it at once, and a screen reader
  // starts from the panel's title (the dialog's and the box's name) and its text, not from the close button.
  const bodyRef = useRef(null);
  return (
    <Transition show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={bodyRef}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-200 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-150 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex justify-end overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-300 motion-reduce:transition-none"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200 motion-reduce:transition-none"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              data-testid={id ? `help-panel-${id}` : undefined}
              className="flex h-full w-full min-w-0 flex-col bg-white shadow-2xl ring-1 ring-slate-900/5 md:w-[36rem] md:max-w-[100vw]"
            >
              <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 py-2 pl-4 pr-2 pt-[max(0.5rem,env(safe-area-inset-top))] md:py-3 md:pl-6 md:pr-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600" aria-hidden="true">
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </span>
                {/* "Guide" belongs to the heading, so the dialog is named "Guide: <title>", not the page's title again. */}
                <Dialog.Title id={titleId} as="h2" className="min-w-0 flex-1 text-base font-semibold leading-6 text-slate-900">
                  <span className="block text-xs font-medium leading-5 text-slate-500">
                    {t("help.common.guide")}
                    <span className="sr-only">: </span>
                  </span>
                  <span className="line-clamp-2 break-words">{title}</span>
                </Dialog.Title>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t("help.common.close")}
                  title={t("help.common.close")}
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-900 md:h-9 md:w-9 ${focusRing}`}
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div
                ref={bodyRef}
                tabIndex={0}
                role="region"
                aria-labelledby={titleId}
                data-testid={id ? `help-body-${id}` : undefined}
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 md:px-6"
              >
                <div className="space-y-6">{children}</div>
              </div>
              <div className="flex shrink-0 justify-end border-t border-slate-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className={`inline-flex h-11 w-full items-center justify-center rounded-lg bg-blue-600 px-5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 md:h-9 md:w-auto ${focusRing}`}
                >
                  {t("help.common.got_it")}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
